export const READ_ONLY_ROLE = 'jbg-read-only'

// Standalone check usable outside components (e.g. displayAlert): the read-only
// role with no batch write scope means a view-only user.
export const isReadOnlyUser = (auth) => {
  const groups = auth?.userGroups || []
  const permissions = auth?.permissions || []
  return groups.includes(READ_ONLY_ROLE) &&
    !groups.includes('billing.admin') &&
    !permissions.includes('billing_batch:create')
}

export const userAccess = {
  computed: {
    entities () {
      return ['billing_batch']
    },
    adminEntities () {
      return [...this.entities, 'key', 'key_value']
    },
    tenant () {
      return this.$auth.tenant
    },
    user () {
      return this.$auth.user
    },
    userGroups () {
      return this.$auth.userGroups || []
    },
    userPermissions () {
      return this.$auth.permissions || []
    },
    userRelatedParties () {
      return this.$store.getters.userRelatedParties
    },
    userRelatedPartyIds () {
      if (this.userRelatedParties.length > 0) {
        return this.userRelatedParties.map(p => p.id)
      }
      return []
    },
    userAdGroupPartyIds () {
      return this.userRelatedParties.flatMap(party => {
        return (party?.party_type_constant === 'AD_GROUP')
          ? party.id
          : []
      })
    },
    userStorePartyIds () {
      return this.userRelatedParties.flatMap(party => {
        return (party?.party_type_constant === 'STORE')
          ? party.id
          : []
      })
    },
    userBatchPartyIds () {
      return [...this.userAdGroupPartyIds, ...this.userStorePartyIds]
    },
    hasPortalAccess () {
      const readScopes = this.entities.map(entity => `${entity}:read`)
      return readScopes.every(scope => this.userPermissions.includes(scope)) || this.userPermissions.includes('recap_additions:read')
    },
    isARGSupport () {
      return this.tenant === 'alliance-retail-group' && this.userGroups.includes('arg-support')
    },
    userAccessiblePortals() {
      const portals = ['ad_planner', 'customer', 'promo', 'item', 'billing', 'tpr', 'partner']
      const isProd = process.env.NODE_ENV === 'production'

      return portals.filter(portal => {
        let readScope = `${portal}:read`
        if (portal === 'billing') {
          readScope = 'billing_batch:read'
        } else if (portal === 'customer') {
          readScope = 'party:read'
        }
        else if (portal === 'tpr') {
          readScope = 'tpr_batch:read'
        }
        else if (portal === 'partner') {
          readScope = 'document:read'
        }
        const canRead = this.hasScope(readScope)
        if (!canRead) return false

        // Hard deny in all envs
        if (this.hasScope(`${portal}:no_access`)) return false

        // Deny in prod only (dev allowed) if they have the prod-block flag
        if (isProd && this.hasScope(`${portal}:no_access_prod`)) return false

        return true
      })
    },
    hasWriteAccess () {
      return this.writePermissions.every(scope => this.userPermissions.includes(scope))
    },
    writePermissions () {
      const actions = ['create', 'read', 'update']
      return this.entities.flatMap(entity => {
        return actions.map(action => `${entity}:${action}`)
      })
    },
    isIproAdmin () {
      return this.userGroups.includes('ipro-admin')
    },
    isARGAdmin () {
      return (this.$auth.tenant === 'alliance-retail-group') && this.userGroups.includes('arg-admin')
    },
    isAdmin () {
      return this.userGroups.includes('billing.admin')
        || this.adminPermissions.every(scope => this.userPermissions.includes(scope))
    },
    isReadOnly () {
      return isReadOnlyUser({ userGroups: this.userGroups, permissions: this.userPermissions })
    },
    adminPermissions () {
      const actions = ['create', 'read', 'update', 'delete']
      return this.adminEntities.flatMap(entity => {
        return actions.map(action => `${entity}:${action}`)
      })
    },
    limitAccessByUserParties () {
      return !this.isAdmin && !this.isIproAdmin && !this.isARGAdmin
    },
    ad_groups () {
      return this.$store.getters.adGroups
    },
    userAdGroups () {
      let groups = [...this.ad_groups]
      if (this.limitAccessByUserParties) {
        groups = groups.filter(group => {
          return this.userAdGroupPartyIds.includes(group?.party_id)
        })
      }
      return groups
    }
  },
  methods: {
    hasScope (scope) {
      return this.userPermissions.includes(scope)
    }
  }
}